/**
 * Auth Store (Zustand)
 * ────────────────────
 * Firebase Auth + server-side access verification.
 *
 * Flow:
 *   1. Google sign-in via Firebase
 *   2. onAuthStateChanged fires
 *   3. Call GET /api/admin?action=check-access with ID token
 *   4. Server checks Firestore invites + creates/updates user profile
 *   5. Client receives: approved, tier, status, isAdmin, trialDaysLeft
 *   6. If not approved → sign out, show waitlist screen
 */
import { create } from 'zustand';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth';
import { auth, isDemoMode } from '@/lib/firebase';
import type { SubscriptionTier } from '@/types/user';

// ─── User-data localStorage keys ─────────────────────────────────────────────
// These are cleared on sign-out and when a different user signs in.
// 'kader-theme' is intentionally excluded — it's a device preference, not user data.
const USER_DATA_KEYS = [
  'kader-schedule',
  'kader-athlete-profile',
  'kader-chat-history',
  'kader-coaching-insights-v2',
  'kader-garmin-data',
  'kader-meals',
  'kader-streak',
  'kader-profile-prefs',
];

function clearUserLocalStorage(): void {
  USER_DATA_KEYS.forEach(key => localStorage.removeItem(key));
  // Reset in-memory Zustand stores (dynamic imports avoid circular deps)
  Promise.all([
    import('./schedule-store').then(m => m.useScheduleStore.setState({ days: {} })),
    import('./streak-store').then(m => m.useStreakStore.setState({
      streakCount: 0, streakStartDate: '', longestStreak: 0,
      lastCheckInDate: '', streakFreezes: 1, freezesWeekOf: '',
      pendingFreezeNotice: false, frozenForStreak: null,
      pendingMilestoneCelebration: null, celebratedMilestones: [],
      xpTotal: 0, earnedBadges: [], pendingLevelUp: null,
    })),
  ]).catch(() => {});
}

const DEMO_USER = {
  uid: 'demo-user',
  email: 'demo@kader.ai',
  displayName: 'Demo Athlete',
  photoURL: null,
  getIdToken: async () => 'demo-token',
} as unknown as User;

interface AuthState {
  user: User | null;
  loading: boolean;
  tier: SubscriptionTier;
  initialized: boolean;
  demoMode: boolean;
  isAdmin: boolean;
  accessDenied: boolean;
  deniedEmail: string | null;
  trialDaysLeft: number | null;
  accessStatus: 'checking' | 'approved' | 'denied' | 'expired' | 'onboarding' | null;

  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
  checkAccess: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: isDemoMode ? DEMO_USER : null,
  loading: !isDemoMode,
  tier: isDemoMode ? 'elite' : 'free',
  initialized: isDemoMode,
  demoMode: isDemoMode,
  isAdmin: isDemoMode,
  accessDenied: false,
  deniedEmail: null,
  trialDaysLeft: null,
  accessStatus: null,

  signInWithGoogle: async () => {
    if (isDemoMode) { set({ user: DEMO_USER, initialized: true }); return; }
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error: any) {
      if (error?.code === 'auth/popup-blocked' || error?.code === 'auth/cancelled-popup-request') {
        await signInWithRedirect(auth, provider);
      } else { throw error; }
    }
  },

  signOut: async () => {
    if (isDemoMode) { set({ user: null }); return; }
    try {
      clearUserLocalStorage();
      localStorage.removeItem('kader-last-uid');
      await firebaseSignOut(auth);
      set({ user: null, tier: 'free', isAdmin: false, accessDenied: false, deniedEmail: null, accessStatus: null, trialDaysLeft: null });
    } catch (error) { console.error('Sign out failed:', error); throw error; }
  },

  getIdToken: async () => {
    const { user, demoMode } = get();
    if (demoMode) return 'demo-token';
    if (!user) return null;
    try { return await user.getIdToken(); }
    catch { return null; }
  },

  checkAccess: async () => {
    const { user } = get();
    if (!user) return;

    // Clear stale data when a different user signs in on the same device
    const lastUid = localStorage.getItem('kader-last-uid');
    if (lastUid && lastUid !== user.uid) {
      console.log('[auth] Different user detected — clearing previous user data');
      clearUserLocalStorage();
    }
    localStorage.setItem('kader-last-uid', user.uid);

    set({ accessStatus: 'checking' });
    try {
      const token = await user.getIdToken();
      const res = await fetch('/api/admin?action=check-access', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();

      if (!data.approved) {
        // Not invited — sign out and show waitlist
        await firebaseSignOut(auth);
        set({
          user: null, loading: false, accessDenied: true,
          deniedEmail: user.email || '', accessStatus: 'denied',
        });
        return;
      }

      // Approved
      set({
        tier: data.tier || 'free',
        isAdmin: data.isAdmin || false,
        trialDaysLeft: data.trialDaysLeft,
        accessStatus: data.status === 'expired' ? 'expired' : data.status === 'onboarding' ? 'onboarding' : 'approved',
        accessDenied: false,
        deniedEmail: null,
      });

      // Load personalized plan from Firestore (async, non-blocking)
      import('./plan-store').then(({ fetchPersonalizedPlan }) => {
        fetchPersonalizedPlan().then(found => {
          if (found) console.log('[auth] Personalized plan loaded from Firestore');
        });
      }).catch(() => {});

      // Load schedule state from Firestore (hydrate localStorage)
      import('./schedule-store').then(({ hydrateFromFirestore }) => {
        hydrateFromFirestore().catch(() => {});
      }).catch(() => {});

      // Load chat history from Firestore
      import('./chat-history-store').then(({ hydrateChatFromFirestore }) => {
        hydrateChatFromFirestore().catch(() => {});
      }).catch(() => {});

      // Hydrate synced activities (Strava/Terra/CSV) + live listener
      import('./garmin-data-store').then(({ hydrateGarminFromFirestore }) => {
        hydrateGarminFromFirestore().catch(() => {});
      }).catch(() => {});

      // Capture timezone for server-side scheduling (weekly review, nudges)
      import('./firestore-sync').then(({ syncProfile }) => {
        try {
          const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (timezone) syncProfile({ timezone }).catch(() => {});
        } catch { /* silent */ }
      }).catch(() => {});

      // Hydrate streak state from Firestore, then refresh weekly freeze budget
      import('./streak-store').then(({ hydrateStreakFromFirestore, useStreakStore }) => {
        hydrateStreakFromFirestore().then(() => {
          useStreakStore.getState().refreshFreezes(data.tier || 'free');
        }).catch(() => {});
      }).catch(() => {});

      // Auto-init user graph + plan if not yet created
      (async () => {
        try {
          const tkn = await user.getIdToken();
          const hdrs = { 'Authorization': `Bearer ${tkn}`, 'Content-Type': 'application/json' };

          // Always load profile from Firestore — never trust stale localStorage
          // (a previous user's profile could still be in localStorage)
          try {
            const { loadProfile } = await import('./firestore-sync');
            const fsProfile = await loadProfile();
            if (fsProfile && fsProfile.name) {
              localStorage.setItem('kader-athlete-profile', JSON.stringify(fsProfile));
              console.log('[auth] Profile loaded from Firestore:', fsProfile.name);
            }
          } catch { /* silent */ }

          // Check if user graph exists
          const graphRes = await fetch('/api/knowledge-graph?action=user', { headers: hdrs });
          const graphData = await graphRes.json();

          if (!graphData.nodes || graphData.nodes.length === 0) {
            // No user graph — init from localStorage profile
            const profileStr = localStorage.getItem('kader-athlete-profile');
            if (profileStr) {
              const profile = JSON.parse(profileStr);
              console.log('[auth] Initializing user graph for', profile.name);
              await fetch('/api/knowledge-graph?action=init-user', {
                method: 'POST', headers: hdrs,
                body: JSON.stringify({ profile }),
              });

              // Also assign personalized plan if not yet assigned
              const planCheck = await fetch('/api/knowledge-graph?action=user', { headers: hdrs });
              const planRes = await fetch('/api/knowledge-graph?action=assign-plan', {
                method: 'POST', headers: hdrs,
                body: JSON.stringify({ profile }),
              });
              const planResult = await planRes.json();
              if (planResult.assigned) {
                console.log(`[auth] Plan assigned: ${planResult._personalized?.templateLabel || 'personalized'}, VDOT ${planResult.vdot}`);
                // Reload plan store with the newly assigned plan
                const { fetchPersonalizedPlan: reload } = await import('./plan-store');
                await reload();
              }
            }
          }
        } catch (err) {
          console.warn('[auth] Auto-init graph/plan failed (non-critical):', err);
        }
      })();
    } catch (err) {
      console.error('[auth] Access check failed:', err);
      // If check fails, allow through in dev mode only
      if (import.meta.env.DEV) {
        set({ tier: 'elite', isAdmin: true, accessStatus: 'approved' });
      } else {
        await firebaseSignOut(auth);
        set({ user: null, accessDenied: true, deniedEmail: user.email || '', accessStatus: 'denied' });
      }
    }
  },
}));

// Auth listener
if (!isDemoMode) {
  getRedirectResult(auth).catch(() => {});

  onAuthStateChanged(auth, async (user) => {
    if (user) {
      useAuthStore.setState({ user, loading: false, initialized: true });
      // Check access on every auth state change
      useAuthStore.getState().checkAccess();
    } else {
      useAuthStore.setState({ user: null, loading: false, initialized: true });
    }
  });
}
